import { uniqBy } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';

import { AppliedFiltersChipsCollectedData } from '~components/Filters/AppliedFiltersChips';
import { FilterMenuOption } from '~components/Filters/FilterMenuItem';

import {
  projectsFilterKeyBySelectedFilter,
  ProjectsSelectedFilter,
} from '../components/Filters/projectsFilterTypes';

type FiltersAvailableOptionsState = Record<ProjectsSelectedFilter, FilterMenuOption[]>;

const AVAILABLE_OPTIONS_STORAGE_KEY = 'projects_availableOptions';

type StoredAvailableOptions = Partial<Record<ProjectsSelectedFilter, FilterMenuOption[]>>;

const getAvailableOptionsInitialState = () => {
  const stored = localStorage.getItem(AVAILABLE_OPTIONS_STORAGE_KEY);
  const parsed = (stored ? JSON.parse(stored) : {}) as StoredAvailableOptions;

  return Object.values(ProjectsSelectedFilter).reduce((obj, filter) => {
    obj[filter] = parsed[filter] ?? [];

    return obj;
  }, {} as FiltersAvailableOptionsState);
};

const getAppliedFilterChipsCollectedDataInitialState = (
  availableOptions: FiltersAvailableOptionsState,
) => {
  const collectedData = Object.entries(availableOptions).reduce((obj, [key, options]) => {
    obj[key as ProjectsSelectedFilter] = options;

    return obj;
  }, {} as AppliedFiltersChipsCollectedData);

  return collectedData;
};

export default function useProjectsFilters() {
  const [availableOptions, setAvailableOptions] = useState(
    getAvailableOptionsInitialState(),
  );

  const [appliedFilterChipsCollectedData, setAppliedFilterChipsCollectedData] = useState(
    getAppliedFilterChipsCollectedDataInitialState(availableOptions),
  );

  const updateAppliedFilterChipsCollectedData = useCallback(
    (filter: string, data: FilterMenuOption[]) => {
      const isValidFilter =
        projectsFilterKeyBySelectedFilter[filter as ProjectsSelectedFilter];

      if (isValidFilter) {
        setAppliedFilterChipsCollectedData((state) => ({
          ...state,
          [filter]: uniqBy([...(state[filter] ?? []), ...data], (i) => i.value),
        }));
      }
    },
    [],
  );

  useEffect(() => {
    const options = Object.entries(availableOptions).reduce((obj, [key, options]) => {
      obj[key as ProjectsSelectedFilter] = options;

      return obj;
    }, {} as StoredAvailableOptions);

    localStorage.setItem(AVAILABLE_OPTIONS_STORAGE_KEY, JSON.stringify(options));
  }, [JSON.stringify(availableOptions)]);

  return {
    availableOptions,
    updateAvailableOptions: setAvailableOptions,
    appliedFilterChipsCollectedData,
    updateAppliedFilterChipsCollectedData,
  };
}
